/**
 * Renders the Bifrost Detail Component.
 *
 * @param {Object} chosenOrg - The chosen organization object.
 * @returns {JSX.Element} The rendered Bifrost Detail Component.
 */
import { useAuth0 } from "@auth0/auth0-react"
import { useEffect, useState } from "react"
import { Checkbox, Feed, FeedContent, FeedEvent, FeedExtra, FeedSummary, Form, Icon, Label, Message, Popup, Segment, SegmentGroup } from "semantic-ui-react"
import { usePostData } from "../../../../Hooks/FetchHooks/PostDataService/usePostData"
import UpgradeFirmwareModal from "../../../../Pages/MainPanel/MainPanelBody/DevicesPage/DeviceDetailsPage/UpgradeFirmwareModal/UpgradeFirmwareModal"
import { isSuperAdminOrAdmin, isVersionHigherOrEqual } from "../../../../Utils/util"
import ConfigureEthernetSettings from "../../../BifrostDeviceDetailsComponents/SettingSegments/ConfigureEthernetSettings/ConfigureEthernetSettings"
import FTP from "../../../BifrostDeviceDetailsComponents/SettingSegments/FTP/FTP"
import BifrostButton from "../../../BifrostElementComponent/BifrostButton/BifrostButton"
import BifrostWaringIcon from "../../../BifrostElementComponent/BifrostWarningIcon/BifrostWaringIcon"
import Loader from "../../Loader/Loader"

const initialState = {
	enableDebug: false,
	otp: false
}

const optionsOtp = [
	{ key: "60", value: 60, text: "1 min" },
	{ key: "300", value: 300, text: "5 min" },
	{ key: "600", value: 600, text: "10 min" },
	{ key: "1800", value: 1800, text: "30 min" },
	{ key: "3600", value: 3600, text: "1 hour" }
]

const initialSettingsState = {
	enableDebug: false,
	otp: optionsOtp[0].value
}

const settingEndpoints = {
	enableDebug: "debugMode",
	otp: "otp"
}

const DeviceSettingsSegment = ({ data: deviceData, deviceConfigData, latestFirmwareVersion, firmwareUpToDate }) => {
	const [settings, setSettings] = useState(initialSettingsState)
	const [values, setValues] = useState(initialSettingsState)
	const [postLoading, setPostLoading] = useState(initialState)
	const [postSuccess, setPostSuccess] = useState(initialState)
	const [postError, setPostError] = useState(initialState)
	const [showEditIcon, setShowEditIcon] = useState(initialState)
	const [changeEnabled, setChangeEnabled] = useState(initialState)
	const [showSyncErrorIcon, setShowSyncErrorIcon] = useState(initialState)

	const { postItem } = usePostData()
	const { user } = useAuth0()

	const enableChange = (field, version) => {
		setPostError({ ...postError, [field]: false })
		if (isVersionHigherOrEqual(deviceData.firmware, version) && deviceData.state === "idle") {
			setChangeEnabled({ ...changeEnabled, [field]: true })
		} else {
			setChangeEnabled({ ...changeEnabled, [field]: false })
		}
	}

	const updateField = (e, name, value) => {
		if (!name) {
			name = e.target.name
			value = e.target.value
		}
		setValues({
			...values,
			[name]: value
		})
	}

	const submitHandler = (field, value) => {
		if (value === undefined) {
			value = values[field]
		}
		resetFieldState(field)

		setPostLoading({ ...postLoading, [field]: true })
		let postObject = {}
		postObject[field] = value
		postObject.serialnumber = deviceData.serialnr

		console.log("postObject: ", postObject)

		postItem("/api/v2/device/configure/" + settingEndpoints[field], postObject, null)
			.then(() => {
				setSettings({ ...settings, [field]: value })
				setPostSuccess({ ...postSuccess, [field]: true })
				setShowSyncErrorIcon({ ...showSyncErrorIcon, [field]: false })
			})
			.catch(() => {
				setPostError({ ...postError, [field]: true })
			})
			.finally(() => {
				setPostLoading({ ...postLoading, [field]: false })
			})
	}

	const resetFieldState = (field) => {
		setPostLoading({ ...postLoading, [field]: false })
		setPostSuccess({ ...postSuccess, [field]: false })
		setPostError({ ...postError, [field]: false })
		setShowEditIcon({ ...showEditIcon, [field]: false })
		setChangeEnabled({ ...changeEnabled, [field]: false })
	}

	const cancelHandler = (field) => {
		setValues({ ...values, [field]: settings[field] })
		resetFieldState(field)
	}

	useEffect(() => {
		let newSettings = { ...settings }
		if (deviceConfigData) {
			setShowSyncErrorIcon({
				...postError,
				enableDebug: deviceConfigData.debug_mode === undefined && true,
				otp: deviceConfigData.otp_ttl === undefined && true
			})
			newSettings.enableDebug = deviceConfigData.debug_mode !== undefined ? deviceConfigData.debug_mode : newSettings.enableDebug
			newSettings.ftp = deviceConfigData.ftp !== undefined ? deviceConfigData.ftp : newSettings.ftp
			newSettings.otp = deviceConfigData.otp_ttl !== undefined ? deviceConfigData.otp_ttl : newSettings.otp
		}
		setSettings(newSettings)
		setValues(newSettings)
	}, [deviceConfigData])

	return (
		<SegmentGroup horizontal style={{ marginTop: "0px" }}>
			<Segment style={{ width: "50%" }}>
				<Feed>
					<FeedEvent className="device-event-margin">
						<FeedContent>
							<FeedSummary>Debug Mode {showSyncErrorIcon.enableDebug && <BifrostWaringIcon content="Could not read Debug Mode from device" />}</FeedSummary>
							<FeedExtra text className={"device-change-disabled"} style={{ justifyContent: "space-between" }}>
								<>
									<div style={{ display: "flex", alignItems: "center" }}>
										<Popup
											disabled={isVersionHigherOrEqual(deviceData.firmware, "v4.9.0")}
											content="Firmware v4.9.0 or higher required"
											wide="very"
											on="click"
											trigger={
												<Checkbox
													disabled={postLoading.enableDebug || deviceData.state !== "idle" || !isVersionHigherOrEqual(deviceData.firmware, "v4.9.0")}
													checked={settings.enableDebug}
													toggle
													onChange={(e, data) => submitHandler("enableDebug", data.checked)}
												/>
											}
										/>
										<div style={{ marginRight: "10px" }}>{settings["enableDebug"] ? "On" : "Off"}</div>
										{postLoading.enableDebug && <Loader backgroundColor="white" inverted size="small" />}
										{postSuccess.enableDebug && <Icon name="check circle outline" size="large" style={{ color: "#39C4AA" }} />}
										{postError.enableDebug && <Icon name="close" size="large" style={{ color: "darkred" }} />}
									</div>
									{postError.enableDebug && (
										<Message negative size="mini" className="small-message">
											Couldn't toggle debug mode
										</Message>
									)}
								</>
							</FeedExtra>
						</FeedContent>
					</FeedEvent>

					<FTP device={deviceData} deviceConfigData={deviceConfigData} />

					{deviceData.type === "AA" && (
						<FeedEvent className="device-event-margin">
							<FeedContent>
								<FeedSummary>OTP Duration {showSyncErrorIcon.otp && <BifrostWaringIcon content="Could not read OTP Duration from device" />}</FeedSummary>
								{postLoading.otp ? (
									<Loader backgroundColor="white" inverted height="1px" minHeight="55px" />
								) : (
									<>
										{postError.otp && <Message error content={"OTP change failed, please try again"} />}

										{!changeEnabled.otp ? (
											<Popup
												disabled={isVersionHigherOrEqual(deviceData.firmware, "v4.9.0") && deviceData.state === "idle"}
												content={!isVersionHigherOrEqual(deviceData.firmware, "v4.9.0") ? "Firmware v4.9.0 or higher required" : "Device must be idle"}
												wide="very"
												on="click"
												trigger={
													<FeedExtra
														text
														onClick={() => {
															isSuperAdminOrAdmin(user) ? enableChange("otp", "v4.9.0") : null
														}}
														className={"user-change-hover"}
														onMouseLeave={() => {
															if (isSuperAdminOrAdmin(user)) {
																setShowEditIcon({ ...showEditIcon, "otp": false })
															}
														}}
														onMouseEnter={() => {
															if (isSuperAdminOrAdmin(user)) {
																setShowEditIcon({ ...showEditIcon, "otp": true })
															}
														}}
													>
														{
															// Find the selected option and display the text
															optionsOtp.find((option) => option.value === settings.otp).text
														}
														{postSuccess.otp && !showEditIcon.otp && <Icon name="check circle outline" size="large" style={{ float: "right", color: "#39C4AA" }} />}
														{showEditIcon.otp && <Icon style={{ float: "right" }} name="edit" />}
													</FeedExtra>
												}
											/>
										) : (
											<FeedExtra className="user-dropdown" style={{ marginTop: "5px" }} text>
												<Form.Dropdown
													style={{ marginBottom: "14px" }}
													fluid
													search
													selection
													defaultValue={settings.otp}
													onChange={(e, { name, value }) => updateField(e, name, value)}
													name="otp"
													placeholder="Select OTP Duration"
													options={optionsOtp}
												/>
												<div style={{ display: "flex", justifyContent: "right" }}>
													<BifrostButton onClick={() => submitHandler("otp")} color="blue" style={{ width: "90px" }}>
														Save
													</BifrostButton>
													<BifrostButton
														color="gray"
														onClick={() => {
															cancelHandler("otp")
														}}
														style={{ width: "90px" }}
													>
														Cancel
													</BifrostButton>
												</div>
											</FeedExtra>
										)}
									</>
								)}
							</FeedContent>
						</FeedEvent>
					)}
				</Feed>
			</Segment>
			<Segment style={{ width: "50%" }}>
				<Feed>
					<FeedEvent className="device-event-margin">
						{/* <FeedLabel image={image} /> */}
						<FeedContent>
							{/* <FeedDate content={date} /> */}
							<FeedSummary content={"Firmware Version"} />
							<FeedExtra
								text
								content={
									deviceData.firmware ? (
										<div style={{ display: "flex", justifyContent: "space-between" }}>
											<div className="device-detail-cell">
												{deviceData.firmware.toString()}
												{latestFirmwareVersion && firmwareUpToDate && (
													<Label style={{ marginLeft: "10px" }} size="small">
														LATEST
													</Label>
												)}
											</div>
											{latestFirmwareVersion && (
												<div style={{ display: "flex", position: "relative" }}>
													<UpgradeFirmwareModal
														disabled={deviceData.state !== "idle"}
														device={deviceData}
														latestFirmwareVersion={latestFirmwareVersion}
														firmwareUpToDate={firmwareUpToDate}
													/>
												</div>
											)}
										</div>
									) : (
										"Unknown"
									)
								}
							/>
						</FeedContent>
					</FeedEvent>
					<ConfigureEthernetSettings />
				</Feed>
			</Segment>
		</SegmentGroup>
	)
}

export default DeviceSettingsSegment
